import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useMediaQuery } from 'react-responsive'

import { FormBuilder, Spinner } from '@acorns/web-components'
import { useRemoteStrings } from '@acorns/web-utils'
import { path } from 'ramda'

import { CURRENT_SIGN_UP_SCREEN_KEY } from 'app-constants'
import AcornsMarkGreenSVG from 'assets/icons/acorns-mark-green.svg'
import Acorns_Logo_and_Flag from 'assets/images/Acorns_Logo_and_Flag.png'
import { Accessibility } from 'components/Accessibility'
import AgreementModal from 'components/AgreementModal'
import CredentialsFormRow from 'components/CredentialsFormRow'
import FormContainer from 'components/FormContainer'
import Layout from 'components/Layout'
import Nav from 'components/Nav'
import { OneTrustLink } from 'components/OneTrust/Link'
import Separator from 'components/Separator'
import { withClickTrack } from 'components/Trackers/ClickTracker'
import { TRACKER_IDS } from 'components/Trackers/trackerIDs'
import { TestIDs } from 'test/utils/testIds'
import theme from 'theme'
import trackEvent from 'utils/analytics/trackEvent'
import { getFlow } from 'utils/FlowProvider/utils'
import { mocOnboardingEnabled } from 'utils/Optimizely/features'
import { shouldGrayOutCTACall } from 'utils/shouldGrayOutCTA'

import { ButtonHandle } from '../Default/GetStartedButton'
import { enhance } from './enhance'
import { GetStartedButton } from './GetStartedButton'
import {
  AlternativeAcornsLogo,
  AlternativeStyledHeroSubtext,
  ButtonContainer,
  LeftAlignAcornImage,
  LinkInCTAContainer,
  StyledButton,
  StyledHeroCaption,
  StyledHeroDescription,
  StyledHeroHeader,
  StyledHeroSubtext,
  StyledSubText,
} from './styled-components'
import { Props } from './types'
import { validate } from './validation'

// Create a trackable Create Account button
const CreateAccountButton = withClickTrack<any, any>(TRACKER_IDS.SIGNUP.CREATE_ACCOUNT_BUTTON)(
  StyledButton,
)
const submitKey = 'enter'
const submitCode = 13

interface AcornsLogoProps {
  isEmailDisplayed: boolean
  isMilitary: boolean
  isMobilePlatform: boolean
}

const AcornsLogo: React.FC<AcornsLogoProps> = (props: AcornsLogoProps) => {
  if (props.isMilitary) {
    return <AlternativeAcornsLogo src={Acorns_Logo_and_Flag} height={25} />
  } else if (props.isEmailDisplayed && props.isMobilePlatform) {
    return <LeftAlignAcornImage src={AcornsMarkGreenSVG} height={51} />
  }
  return null
}

export const Signup: React.FC<Props> = props => {
  const {
    currentAgreement,
    env,
    errors,
    isLoading,
    isSubmitting,
    isValid,
    heroText,
    onChange,
    values,
    isMilitary,
    isMOCAttribution,
  } = props

  const shouldGrayOutPasswordCTA = shouldGrayOutCTACall({
    values: values,
    validation: v => validate(v, props),
    ignoredKeys: ['email'],
  })

  const shouldGrayOutEmailCTA = shouldGrayOutCTACall({
    values: values,
    validation: v => validate(v, props),
    ignoredKeys: ['password', 'usResident', 'partnerDisclosure'],
  })

  const { getRemoteString } = useRemoteStrings()
  const isMOCFunnelEnabled = props.isFeatureEnabled(mocOnboardingEnabled)
  const [isEmailRowDisplayed, setIsEmailRowDisplayed] = useState<boolean>(true)
  const isMobilePlatform = useMediaQuery({ maxWidth: `${theme.breakpoints.sm}px` })
  const onBackOverride = () => setIsEmailRowDisplayed(true)
  const getStartedButtonRef = useRef<ButtonHandle>(null)
  const handleOnKeyDown = useCallback(
    event => {
      // Submit key is tied to keycode
      // keycode 13 is the Enter key
      // https://www.toptal.com/developers/keycode
      if (event.key?.toLowerCase() === submitKey || event.code === submitCode) {
        getStartedButtonRef?.current?.click()
      }
    },
    [getStartedButtonRef],
  )
  const isNavDisplayed = isMilitary || !(isEmailRowDisplayed && isMobilePlatform)

  useEffect(() => {
    if (!isEmailRowDisplayed) {
      trackEvent('Screen Viewed', {
        eventData: {
          object_name: 'registrationCreateAccountPassword',
          screen: 'registrationCreateAccountPassword',
          screen_name: 'registrationCreateAccountPassword',
        },
        flow: props.isProspectOnDirectToMobile ? 'directToMobile' : getFlow(),
      })
    }

    // used by Accessibility tracking
    sessionStorage.setItem(CURRENT_SIGN_UP_SCREEN_KEY, isEmailRowDisplayed ? 'email' : 'password')
    return () => sessionStorage.removeItem(CURRENT_SIGN_UP_SCREEN_KEY)
  }, [isEmailRowDisplayed])

  const onFocus = () =>
    trackEvent('Field Clicked', {
      eventData: {
        object_name: 'registrationCreateAccountEmailFocus',
        screen: 'registrationCreateAccount',
        screen_name: 'registrationCreateAccount',
      },
      flow: getFlow(),
    })

  return (
    <>
      {isNavDisplayed ? (
        <Nav
          back={!isEmailRowDisplayed}
          onBackOverride={onBackOverride}
          showAcornsLogo={isEmailRowDisplayed}
        />
      ) : (
        <Accessibility zIndex={2} />
      )}
      <Layout
        header={props.header}
        text={props.text}
        footnote={props.footnote}
        footnoteLock={props.footnoteLock}
        pageNotFound={props.pageNotFound}
        zestyOverride={props.zestyOverride}
        showOneTrustLink={!isEmailRowDisplayed}
      >
        <AcornsLogo
          isEmailDisplayed={isEmailRowDisplayed}
          isMilitary={isMilitary}
          isMobilePlatform={isMobilePlatform}
        />
        <StyledHeroHeader isEmailRowDisplayed={isEmailRowDisplayed} isMilitary={isMilitary}>
          {isEmailRowDisplayed ? (
            isMilitary ? (
              heroText.veteranText
            ) : (
              heroText.text
            )
          ) : (
            <>
              <strong>Let's create your password for {values.email}</strong>
            </>
          )}
          {isEmailRowDisplayed && isMOCFunnelEnabled && isMOCAttribution && (
            <StyledSubText>
              {getRemoteString({ key: 'registration.moc_attribution_quote' })}
            </StyledSubText>
          )}
        </StyledHeroHeader>
        {isMilitary && (
          <StyledHeroDescription>
            <StyledHeroSubtext>{heroText.veteranSubtext}</StyledHeroSubtext>
          </StyledHeroDescription>
        )}
        <FormContainer>
          <CredentialsFormRow
            errors={errors}
            onChange={onChange}
            onEmailChange={props.onEmailChange}
            values={values}
            isEmailRowDisplayed={isEmailRowDisplayed}
            onFocus={onFocus}
            onKeyDown={handleOnKeyDown}
          />

          {!isEmailRowDisplayed && (
            <FormBuilder
              errors={errors}
              rows={props.disclosureRow}
              values={values}
              handleChange={onChange}
              setFieldValue={props.setFieldValue}
            />
          )}
        </FormContainer>
        {isMilitary ? (
          <StyledHeroCaption>
            <AlternativeStyledHeroSubtext>{heroText.veteranCaption}</AlternativeStyledHeroSubtext>
          </StyledHeroCaption>
        ) : null}

        <Separator />
        <ButtonContainer>
          {/** We can't simply unmount it. OneTrust will try to read it at the first render, if it's not in the DOM, the eventListeners will never be added. All we can do is hide it. */}
          <LinkInCTAContainer show={!isEmailRowDisplayed}>
            <OneTrustLink />
          </LinkInCTAContainer>
          {isEmailRowDisplayed ? (
            <GetStartedButton
              formValues={values}
              getSourceAttributes={props.getSourceAttributes}
              isEmailValid={props.isEmailValid && !shouldGrayOutEmailCTA}
              isProspectOnDirectToMobile={props.isProspectOnDirectToMobile}
              isSubmitting={isSubmitting}
              partner={props.partner}
              setErrors={props.setErrors}
              setIsEmailRowDisplayed={setIsEmailRowDisplayed}
              setIsEmailValid={props.setIsEmailValid}
              setSubmitEmailCount={props.setSubmitEmailCount}
              submitEmailCount={props.submitEmailCount}
              validateForm={props.validateForm}
              inputRef={getStartedButtonRef}
            />
          ) : (
            <CreateAccountButton
              styleType={!isValid || shouldGrayOutPasswordCTA ? 'invalid' : undefined}
              onPress={props.onSubmit}
              data-testid={TestIDs.SIGNUP.CREATE_ACCOUNT_BUTTON}
              disabled={isSubmitting}
              buttonType={'submit'}
            >
              {isSubmitting ? <Spinner height={20} width={20} white={true} /> : props.submitCta}
            </CreateAccountButton>
          )}
        </ButtonContainer>
        <AgreementModal
          title={path(['name'], currentAgreement)}
          isOpen={props.isModalOpen}
          onCloseClick={props.closeAgreementModal}
        >
          {isLoading ? <Spinner height={50} width={50} /> : path(['document'], currentAgreement)}
        </AgreementModal>
      </Layout>
    </>
  )
}

export default enhance(Signup)
